'use client';

import { ChevronDown, Moon, Sun } from 'lucide-react';
import { useTranslations } from 'next-intl';
import * as React from 'react';
import { useMyLook } from '@/lib/api/auth/hooks';
import { KycSeal } from '@/lib/components/Kyc/KycSeal';
import { GlowName } from '@/lib/components/RewardsHub/GlowName';
import { NAV_GIFT } from '@/lib/components/RewardsHub/navGift';
import { useThemeTransition } from '@/lib/hooks/useThemeTransition';
import { FadeInGroup, FadeInItem } from '@/lib/ui-kit/fade-in';
import { Icon, type IconName } from '@/lib/ui-kit/icons';
import { cn } from '@/lib/ui-kit/utils';
import { Button } from '@/lib/ui-kit-v2/button';
import { type LanguageOption, LanguageRow } from '@/lib/ui-kit-v2/language-row';
import { UserAvatar } from '@/lib/ui-kit-v2/user-avatar';
import { MASKED_EMAIL } from '@/lib/utils/maskEmail';
import { isSyntheticEmail } from '@/lib/utils/syntheticEmail';

interface AccountMenuUser {
  id?: string;
  name?: string;
  email?: string;
  avatarUrl?: string;
}

interface AccountMenuProps {
  user: AccountMenuUser;
  /** Passed KYC — shows the blue verification seal before the name. */
  isVerified?: boolean;
  language?: string;
  flag?: React.ReactNode;
  onLanguageClick?: () => void;
  languageOptions?: LanguageOption[];
  currentLanguageCode?: string;
  onLanguageChange?: (code: string) => void;
  onProfileClick?: () => void;
  onWalletClick?: () => void;
  /** Shown below 1024px, where the header nav (and its Rewards Hub entry) is hidden. */
  onRewardsHubClick?: () => void;
  onPromocodesClick?: () => void;
  onLeaderboardClick?: () => void;
  onNewsClick?: () => void;
  onApiClick?: () => void;
  onSettingsClick?: () => void;
  onHelpCenterClick?: () => void;
  /** Opens the complete-account flow. Shown in place of the email for SSO
   *  accounts that still carry a synthetic (wallet / Telegram / Steam) email. */
  onAddEmail?: () => void;
  onLogout?: () => void;
  className?: string;
}

function AccountMenu({
  user,
  isVerified = false,
  language = 'En',
  flag,
  onLanguageClick,
  languageOptions,
  currentLanguageCode,
  onLanguageChange,
  onProfileClick,
  onWalletClick,
  onRewardsHubClick,
  onPromocodesClick,
  onLeaderboardClick,
  onNewsClick,
  onApiClick,
  onSettingsClick,
  onHelpCenterClick,
  onAddEmail,
  onLogout,
  className,
}: AccountMenuProps) {
  const t = useTranslations('nav');
  const [isEmailRevealed, setIsEmailRevealed] = React.useState(false);
  const isSyntheticAccount = isSyntheticEmail(user.email);
  const look = useMyLook();
  return (
    <FadeInGroup
      staggerDelay={0.04}
      className={cn(
        'flex w-[260px] flex-col gap-4 rounded-[10px] border border-solid border-v2-border bg-v2-surface-secondary p-5',
        'shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)]',
        className,
      )}
    >
      <FadeInItem yOffset={8} duration={0.2}>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onProfileClick}
            aria-label={user.name ?? 'Account'}
            className="shrink-0 cursor-pointer rounded-full outline-none transition-opacity hover:opacity-90"
          >
            <UserAvatar
              userId={user.id}
              name={user.name}
              src={user.avatarUrl}
              look={look}
              size={36}
            />
          </button>
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <div className="flex min-w-0 items-center gap-1">
              {isVerified && <KycSeal verified size={20} />}
              <button
                type="button"
                onClick={onProfileClick}
                className="min-w-0 max-w-full cursor-pointer self-start truncate text-left text-[13px] font-[500] leading-4 tracking-[-0.13px] text-v2-text-primary outline-none transition-colors hover:text-v2-bg-blue"
              >
                <GlowName glow={look?.glow ?? null}>{user.name ?? 'Account'}</GlowName>
              </button>
            </div>
            {isSyntheticAccount ? (
              <Button
                appearance="accent"
                size="S"
                onClick={onAddEmail}
                className="mt-2 h-7 self-start px-3 text-[11px]"
              >
                {t('addEmail')}
              </Button>
            ) : (
              user.email && (
                <button
                  type="button"
                  onClick={() => setIsEmailRevealed((prev) => !prev)}
                  className="group flex max-w-full cursor-pointer items-center self-start truncate rounded-[6px] outline-none transition-colors"
                  aria-label={isEmailRevealed ? 'Hide email' : 'Reveal email'}
                >
                  <span
                    className={cn(
                      'truncate text-[13px] font-normal leading-5 text-v2-text-secondary transition-colors group-hover:text-v2-text-primary',
                      !isEmailRevealed && 'tracking-[1px]',
                    )}
                  >
                    {isEmailRevealed ? user.email : MASKED_EMAIL}
                  </span>
                </button>
              )
            )}
          </div>
        </div>
      </FadeInItem>

      <FadeInItem yOffset={8} duration={0.2}>
        <Divider />
      </FadeInItem>

      <FadeInItem yOffset={8} duration={0.2}>
        <IconRow icon="CreditCard" label={t('wallet')} onClick={onWalletClick} />
      </FadeInItem>
      {onRewardsHubClick && (
        <FadeInItem yOffset={8} duration={0.2} className="min-[1024px]:hidden">
          <IconRow
            icon={<img src={NAV_GIFT} alt="" className="size-4 shrink-0" draggable={false} />}
            label={t('rewardsHub')}
            onClick={onRewardsHubClick}
          />
        </FadeInItem>
      )}
      {onPromocodesClick && (
        <FadeInItem yOffset={8} duration={0.2}>
          <IconRow icon="Ticket" label={t('promocodes')} onClick={onPromocodesClick} />
        </FadeInItem>
      )}
      <FadeInItem yOffset={8} duration={0.2}>
        <IconRow icon="Insight" label={t('leaderboard')} onClick={onLeaderboardClick} />
      </FadeInItem>
      <FadeInItem yOffset={8} duration={0.2}>
        <IconRow icon="Newspaper" label={t('news')} onClick={onNewsClick} />
      </FadeInItem>
      <FadeInItem yOffset={8} duration={0.2}>
        <IconRow icon="Key2" label={t('api')} onClick={onApiClick} external />
      </FadeInItem>
      <FadeInItem yOffset={8} duration={0.2}>
        <IconRow icon="Settings2" label={t('settings')} onClick={onSettingsClick} />
      </FadeInItem>
      <FadeInItem yOffset={8} duration={0.2}>
        <IconRow icon="Hexagon" label={t('helpCenter')} onClick={onHelpCenterClick} />
      </FadeInItem>

      <FadeInItem yOffset={8} duration={0.2}>
        <Divider />
      </FadeInItem>

      <FadeInItem yOffset={8} duration={0.2}>
        <div className="flex w-full items-center gap-2 py-1">
          <span className="flex-1 text-[13px] font-normal leading-4 tracking-[-0.13px] text-v2-text-primary">
            {t('theme')}
          </span>
          <ThemeToggle />
        </div>
      </FadeInItem>

      <FadeInItem yOffset={8} duration={0.2}>
        {languageOptions && languageOptions.length > 0 && onLanguageChange ? (
          <LanguageRow
            options={languageOptions}
            selectedCode={currentLanguageCode ?? languageOptions[0]?.code ?? 'en'}
            onSelect={onLanguageChange}
            label={t('language')}
          />
        ) : (
          <button
            type="button"
            onClick={onLanguageClick}
            className="group flex w-full cursor-pointer items-center gap-2 py-1 outline-none"
          >
            <span className="flex-1 text-left text-[13px] font-normal leading-4 tracking-[-0.13px] text-v2-text-primary group-hover:text-v2-bg-blue">
              {t('language')}
            </span>
            <span className="flex shrink-0 items-center gap-1">
              <span className="h-3 w-[18px] shrink-0 overflow-hidden rounded-[2px] shadow-[0_1px_2px_rgba(0,0,0,0.15)] dark:shadow-none">
                {flag ?? <FlagUK />}
              </span>
              <span className="flex items-center gap-0.5 text-[13px] font-normal leading-4 tracking-[-0.13px] text-v2-text-primary">
                {language.toUpperCase()}
                <ChevronDown className="size-4 text-v2-icon-primary" strokeWidth={2} />
              </span>
            </span>
          </button>
        )}
      </FadeInItem>

      <FadeInItem yOffset={8} duration={0.2}>
        <button
          type="button"
          onClick={onLogout}
          className="flex w-full cursor-pointer items-center py-1 text-left outline-none"
        >
          <span className="text-[13px] font-normal leading-4 tracking-[-0.13px] text-v2-text-primary transition-colors hover:text-v2-bg-blue">
            {t('logout')}
          </span>
        </button>
      </FadeInItem>
    </FadeInGroup>
  );
}

function IconRow({
  icon,
  label,
  onClick,
  external = false,
}: {
  /** An icon name, or ready art (the Rewards Hub gift). */
  icon: IconName | React.ReactElement;
  label: string;
  onClick?: () => void;
  /** Opens off-site (new tab): Figma marks it with the 12px link glyph. */
  external?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex w-full cursor-pointer items-center gap-2 py-1 text-left outline-none"
    >
      {typeof icon === 'string' ? (
        <Icon
          name={icon}
          size={16}
          className="shrink-0 text-v2-icon-primary group-hover:text-v2-bg-blue"
        />
      ) : (
        icon
      )}
      <span className="text-[13px] font-normal leading-4 tracking-[-0.13px] text-v2-text-primary group-hover:text-v2-bg-blue">
        {label}
      </span>
      {external && (
        <Icon
          name="Link2"
          size={12}
          className="-ml-1 shrink-0 text-v2-icon-primary group-hover:text-v2-bg-blue"
        />
      )}
    </button>
  );
}

function Divider() {
  return <div aria-hidden className="h-px w-full bg-v2-border" />;
}

function ThemeToggle() {
  const { resolvedTheme, setThemeAnimated } = useThemeTransition();
  const isLight = resolvedTheme !== 'dark';

  return (
    <div className="flex items-center gap-1 rounded-full border border-solid border-v2-btn-secondary-2">
      <ThemeButton
        active={isLight}
        onClick={() => setThemeAnimated('light')}
        aria-label="Light theme"
      >
        <Sun className="size-[14px]" strokeWidth={1.6} />
      </ThemeButton>
      <ThemeButton
        active={!isLight}
        onClick={() => setThemeAnimated('dark')}
        aria-label="Dark theme"
      >
        <Moon className="size-[14px]" strokeWidth={1.6} />
      </ThemeButton>
    </div>
  );
}

function ThemeButton({
  active,
  children,
  onClick,
  ...props
}: {
  active?: boolean;
  children: React.ReactNode;
  onClick?: () => void;
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onClick' | 'children'>) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex size-[22px] cursor-pointer items-center justify-center rounded-full p-1 text-v2-text-primary outline-none transition-all duration-150',
        active ? 'bg-v2-btn-secondary-2 opacity-100' : 'opacity-50 hover:opacity-80',
      )}
      {...props}
    >
      {children}
    </button>
  );
}

function FlagUK() {
  return (
    <svg viewBox="0 0 60 60" className="size-full" aria-hidden>
      <rect width="60" height="60" fill="#012169" />
      <path d="M0 0 L60 60 M60 0 L0 60" stroke="#FFFFFF" strokeWidth="12" />
      <path d="M0 0 L60 60 M60 0 L0 60" stroke="#C8102E" strokeWidth="6" />
      <path d="M30 0 V60 M0 30 H60" stroke="#FFFFFF" strokeWidth="20" />
      <path d="M30 0 V60 M0 30 H60" stroke="#C8102E" strokeWidth="10" />
    </svg>
  );
}

export type { AccountMenuProps, AccountMenuUser };
export { AccountMenu };
